import { useEffect, useRef, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import { breakpointsRaw } from '~/theme/breakpoints';
import { Brands } from '~/lib/data-contract';
import { getCarouselSettings } from './getCarouselSettings';

export function useGetCarouselOptions(items: Brands[]) {
    const totalItems = items.length;
    const { desktop, tablet, mobile } = getCarouselSettings(totalItems);
    const isOdd = totalItems % 2 !== 0;

    const useSlider = (totalItems === 6 && isOdd) || totalItems === 5 || totalItems > 6;

    const leftArrow = useRef<HTMLButtonElement | null>(null);
    const rightArrow = useRef<HTMLButtonElement | null>(null);

    const [windowWidth, setWindowWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 0,
    );

    const [isDesktop, setIsDesktop] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useDebounce(
        () => {
            setIsDesktop(windowWidth >= breakpointsRaw.md);
            setIsTablet(windowWidth >= breakpointsRaw.sm && windowWidth < breakpointsRaw.md);
            setIsMobile(windowWidth < breakpointsRaw.sm);
        },
        200,
        [windowWidth],
    );

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isButtonHidden =
        (totalItems === 5 && isDesktop) || (totalItems === 3 && (isDesktop || isTablet));

    const shouldShowButtons = useSlider && !(isMobile || isButtonHidden);

    const getCarouselOptions = () => {
        if (!useSlider && totalItems !== 3) {
            return {
                spaceBetween: 0,
                allowTouchMove: false,
                slidesPerView: mobile.slidePerView,
                grid: {
                    rows: mobile.numberOfRows,
                    fill: 'row' as const,
                },
                breakpoints: {
                    [breakpointsRaw.sm]: {
                        slidesPerView: tablet.slidePerView,
                        grid: {
                            fill: 'row' as const,
                            rows: tablet.numberOfRows,
                        },
                    },
                    [breakpointsRaw.md]: {
                        slidesPerView: desktop.slidePerView,
                        grid: {
                            fill: 'row' as const,
                            rows: desktop.numberOfRows,
                        },
                    },
                },
            };
        }

        return {
            spaceBetween: 0,
            allowTouchMove: true,
            slidesPerView: mobile.slidePerView,
            breakpoints: {
                [breakpointsRaw.sm]: {
                    slidesPerView: tablet.slidePerView,
                },
                [breakpointsRaw.md]: {
                    slidesPerView: desktop.slidePerView,
                },
            },
        };
    };

    return {
        leftArrow,
        rightArrow,
        isMobile,
        isTablet,
        isDesktop,
        desktop,
        tablet,
        useSlider,
        shouldShowButtons,
        getCarouselOptions,
    };
}
